'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductOption, ProductVariant } from '@repo/shopify';
import { getColorSwatchStyle } from './product-option-utils';
interface VariantSelectorProps {
  options: ProductOption[];
  selectedOptions: Record<string, string>;
  variants: ProductVariant[];
  onOptionChange: (name: string, value: string) => void;
  hiddenOptionNames?: string[];
  /** Currently selected SOLD-OUT option value (e.g. a sold-out size) driving the notify-me prompt. */
  soldOutSelection?: {
    name: string;
    value: string;
  } | null;
  /** Called when a shopper taps a sold-out (non-color) option value to request a restock alert. */
  onSelectSoldOut?: (name: string, value: string) => void;
  /** The variant list is known-incomplete (e.g. a card's variants(first: 1)
   * while the live fetch is in flight) — render every value as available
   * instead of striking out combos we simply haven't loaded yet. */
  availabilityUnknown?: boolean;
}
export function VariantSelector(t0) {
  const $ = _c(25);
  const {
    options,
    selectedOptions,
    variants,
    onOptionChange,
    hiddenOptionNames: t1,
    soldOutSelection: t2,
    onSelectSoldOut,
    availabilityUnknown: t3
  } = t0;
  let availabilityUnknown;
  let handleOptionChange;
  let soldOutSelection;
  let t4;
  if ($[0] !== onOptionChange || $[1] !== t1 || $[2] !== t2 || $[3] !== t3) {
    const hiddenOptionNames = t1 === undefined ? [] : t1;
    soldOutSelection = t2 === undefined ? null : t2;
    availabilityUnknown = t3 === undefined ? false : t3;
    let t5;
    if ($[8] !== onOptionChange) {
      t5 = (name, value) => {
        onOptionChange(name, value);
      };
      $[8] = onOptionChange;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    handleOptionChange = t5;
    t4 = new Set(hiddenOptionNames.map(_temp));
    $[0] = onOptionChange;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = availabilityUnknown;
    $[5] = handleOptionChange;
    $[6] = soldOutSelection;
    $[7] = t4;
  } else {
    availabilityUnknown = $[4];
    handleOptionChange = $[5];
    soldOutSelection = $[6];
    t4 = $[7];
  }
  const hiddenOptions = t4;
  let t5;
  if ($[10] !== availabilityUnknown || $[11] !== selectedOptions || $[12] !== variants) {
    t5 = (optionName, optionValue) => {
      if (availabilityUnknown) {
        return true;
      }
      const ignoreOtherSelections = optionName.toLowerCase() === "color";
      return variants.some(variant => {
        if (!variant.availableForSale) {
          return false;
        }
        return variant.selectedOptions.every(opt => {
          if (opt.name === optionName) {
            return opt.value === optionValue;
          }
          if (ignoreOtherSelections) {
            return true;
          }
          const selectedValue = selectedOptions[opt.name];
          return !selectedValue || selectedValue === opt.value;
        });
      });
    };
    $[10] = availabilityUnknown;
    $[11] = selectedOptions;
    $[12] = variants;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const isOptionAvailable = t5;
  let t6;
  if ($[14] !== handleOptionChange || $[15] !== hiddenOptions || $[16] !== isOptionAvailable || $[17] !== onSelectSoldOut || $[18] !== options || $[19] !== selectedOptions || $[20] !== soldOutSelection?.name || $[21] !== soldOutSelection?.value) {
    t6 = options.map(option => {
      if (hiddenOptions.has(option.name.toLowerCase())) {
        return null;
      }
      if (option.values.length <= 1) {
        return null;
      }
      const isColor = option.name.toLowerCase() === "color";
      return <div key={option.id}><div className="mb-2"><span className="text-sm font-bold uppercase tracking-wide text-neutral-900">{option.name}:{selectedOptions[option.name] && <span className="ml-2 font-normal normal-case tracking-normal text-neutral-900">{selectedOptions[option.name]}</span>}</span></div>{isColor ? <div className="flex flex-wrap gap-2">{[...option.values].sort(_temp2).map(value_0 => {
            const isSelected = selectedOptions[option.name] === value_0;
            const available = isOptionAvailable(option.name, value_0);
            return <button key={value_0} type="button" className={`group relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full p-0 transition-opacity ${available ? "" : "opacity-40"}`} onClick={() => handleOptionChange(option.name, value_0)} title={value_0} disabled={!available}><span className={`block h-8 w-8 shrink-0 rounded-full border border-neutral-300 ${isSelected ? "ring-2 ring-neutral-900 ring-offset-2" : available ? "group-hover:ring-2 group-hover:ring-neutral-300 group-hover:ring-offset-2" : ""}`} style={getColorSwatchStyle(value_0, options)} />{!available && <span className="absolute inset-0 flex items-center justify-center"><span className="block h-10 w-px rotate-45 bg-neutral-400" /></span>}</button>;
          })}</div> : <div className="grid gap-1.5" style={{
          gridTemplateColumns: `repeat(${option.values.length > 5 ? 4 : option.values.length}, minmax(0, 1fr))`
        }}>{option.values.map(value_1 => {
            const available_0 = isOptionAvailable(option.name, value_1);
            const groupHasSoldOutSelection = soldOutSelection?.name === option.name;
            const isSoldOutSelected = groupHasSoldOutSelection && soldOutSelection?.value === value_1;
            if (!available_0) {
              return <button key={value_1} type="button" aria-label={`${value_1} — sold out. Get notified when it's back in stock.`} aria-pressed={isSoldOutSelected} className={`relative overflow-hidden rounded border px-3 py-2.5 text-sm font-semibold opacity-40 transition-colors ${isSoldOutSelected ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-neutral-50 text-neutral-900 hover:border-neutral-900"}`} onClick={() => onSelectSoldOut?.(option.name, value_1)}>{value_1}<svg aria-hidden="true" className={`pointer-events-none absolute inset-0 h-full w-full ${isSoldOutSelected ? "text-white" : "text-neutral-500"}`} preserveAspectRatio="none" viewBox="0 0 100 100"><line x1="0" y1="100" x2="100" y2="0" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" /></svg></button>;
            }
            const isSelected_0 = !groupHasSoldOutSelection && selectedOptions[option.name] === value_1;
            return <button key={value_1} type="button" className={`rounded border px-3 py-2.5 text-sm font-semibold transition-colors ${isSelected_0 ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-white text-neutral-900 hover:border-neutral-900"}`} onClick={() => handleOptionChange(option.name, value_1)}>{value_1}</button>;
          })}</div>}</div>;
    });
    $[14] = handleOptionChange;
    $[15] = hiddenOptions;
    $[16] = isOptionAvailable;
    $[17] = onSelectSoldOut;
    $[18] = options;
    $[19] = selectedOptions;
    $[20] = soldOutSelection?.name;
    $[21] = soldOutSelection?.value;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t6) {
    t7 = <div className="space-y-4">{t6}</div>;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp2(a, b) {
  return a.localeCompare(b);
}
function _temp(name_0) {
  return name_0.toLowerCase();
}